/* 파이프라인 제어기. 데모와 MCP 가 같은 엔진(run.ts 의 runDemo)을 탄다. 다른 점은 모델 포트 하나뿐이다.
 * 데모는 Ollama 를 포트에 꽂고, MCP 는 이 제어기의 대기 포트를 꽂는다: 엔진이 모델에게 물을 때마다 요청을 하나 세워 두고, 호스트 모델의 응답이 들어올 때까지 기다린다.
 * 단계의 순서, 지시문, 스키마, 대조, 되먹임, 판정은 엔진이 정하므로 호스트 모델의 종류와 무관하다. 호스트는 요청에 답하는 일만 한다 */
import { jsonBody } from './jsonbody';
import type { ChatMessage, ModelPort } from './model';
import { runDemo } from './run';
import type { Hooks, Meta } from './run';
import { clearScene, waitForCurves } from './style';
import type { MathcoreApp } from './tools';

export interface HostAsk {
  id: number;
  /** 단계 이름(풀이와 그림 계획, 값 옮기기, 입력 개체, 구성 사용법과 args 등) */
  label: string;
  /** json 이면 schema 를 따르는 JSON 하나, text 이면 글 */
  kind: 'json' | 'text';
  messages: ChatMessage[];
  schema: unknown;
}

export type PipelineState =
  | ({ state: 'ask' } & HostAsk)
  | { state: 'done'; stamp: string; meta: Partial<Meta>; solution: string; log: string[]; stages: StageShot[]; stepShots: StepShot[]; frames: FrameShot[] }
  | { state: 'error'; message: string; log: string[] }
  | { state: 'wait' }
  | { state: 'idle' };

const LOG_LIMIT = 400;

/** 생성 단계 하나가 끝난 직후의 화면. 사용자 지시(2026-10-19): 풀이 단계별 스크린샷을 함께 낸다. 카메라는 끝 화면과 같다 */
export interface StageShot { label: string; png: string; caption?: string; at?: number }
/** 풀이 단계(1부터)가 선언한 개체가 전부 선 순간의 캡처 */
export interface StepShot { step: number; label: string; names: string[]; png: string; at: number }
/** 도구 호출 하나가 선 뒤의 화면. 내보내기 재생기의 프레임 */
export interface FrameShot { label: string; names: string[]; png: string; at: number }

/** 그림판 층(.canvas canvas)을 흰 바탕 위에 합쳐 PNG base64 로 만든다. 실행기의 캡처(worker/session.ts)와 같은 영역이다 */
function snapshotPng(): string {
  const layers = [...document.querySelectorAll<HTMLCanvasElement>('.canvas canvas')];
  if (!layers.length) return '';
  const out = document.createElement('canvas');
  out.width = layers[0].width; out.height = layers[0].height;
  const g = out.getContext('2d');
  if (!g) return '';
  g.fillStyle = '#fff'; g.fillRect(0, 0, out.width, out.height);
  for (const layer of layers) g.drawImage(layer, 0, 0);
  return out.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
}

/** 단계마다 저장 문자열(dataManager.saveAsString)을 남기고, 끝에서 하나씩 복원해 끝 카메라로 찍은 뒤 끝 상태를 되돌린다.
 * 원개체 visible 은 다각형과 측정에 안 먹혀(실측 2026-10-19) 쓰지 않는다. 복원 뒤 잰 값도 그대로다(실측: 넓이 20) */
async function reshootStages(app: MathcoreApp, stages: { label: string; data: string }[]): Promise<StageShot[]> {
  const data: any = app.dataManager;
  const camera: any = app.cameraManager;
  if (!stages.length || typeof data?.saveAsString !== 'function' || typeof data?.load !== 'function' || typeof camera?.getCameraData !== 'function') return [];
  const finalData: string = data.saveAsString();
  const cam = camera.getCameraData();
  const out: StageShot[] = [];
  const settle = async () => { await waitForCurves(app); await new Promise<void>((done) => { setTimeout(done, 400); }); };
  try {
    for (const stage of stages) {
      await data.load(stage.data);
      camera.setCameraData(cam);
      await settle();
      const png = snapshotPng();
      if (png) out.push({ label: stage.label, png });
    }
  } finally {
    await data.load(finalData);
    camera.setCameraData(cam);
    await settle();
  }
  return out;
}

export function createPipeline(app: MathcoreApp, afterExecute?: () => void | Promise<void>) {
  let seq = 0;
  let pending: { ask: HostAsk; resolve: (content: string) => void; reject: (error: Error) => void } | null = null;
  let outcome: PipelineState | null = null;
  let running: AbortController | null = null;
  let waiters: (() => void)[] = [];
  const wake = () => { const list = waiters; waiters = []; for (const w of list) w(); };

  const port: ModelPort = (messages, options) => new Promise((resolve, reject) => {
    seq += 1;
    const ask: HostAsk = { id: seq, label: options.label ?? '', kind: options.format ? 'json' : 'text', messages, schema: options.format ?? null };
    pending = {
      ask,
      resolve: (content) => resolve({ role: 'assistant', content, thinking: '', doneReason: 'stop' }),
      reject,
    };
    options.signal.addEventListener('abort', () => reject(new Error('멈췄습니다.')), { once: true });
    wake();
  });

  const cancel = () => {
    running?.abort();
    running = null;
    pending = null;
    outcome = null;
    wake();
  };

  /** 실행 본체. 끝나면 outcome 을 채운다 */
  const launch = (problem: string, variantElements: string[] | undefined, controller: AbortController): void => {
      const log: string[] = [];
      let meta: Partial<Meta> = {};
      let solution = '';
      const stages: StageShot[] = [];
      const stageData: { label: string; data: string }[] = [];
      const stepData: { step: number; label: string; names: string[]; data: string; png: string; at: number }[] = [];
      const frameData: { label: string; names: string[]; data: string; png: string; at: number }[] = [];
      const startedAt = performance.now();
      const hooks: Hooks = {
        push: (kind, text) => { log.push(`[${kind}] ${text}`); if (log.length > LOG_LIMIT) log.shift(); },
        replaceThinking: () => {},
        setStatus: () => {},
        setSolution: (markdown) => { solution = markdown; },
        setMeta: (part) => { meta = { ...meta, ...part }; },
        afterExecute: async (label) => {
          const t0 = performance.now();
          await afterExecute?.();
          const t1 = performance.now();
          await new Promise<void>((done) => { setTimeout(done, 300); });
          const png = snapshotPng();
          const t2 = performance.now();
          const name = label.replace(' 사용법과 args', '');
          if (png) stages.push({ label: name, png });
          const saved: unknown = (app.dataManager as any)?.saveAsString?.();
          if (typeof saved === 'string' && saved) stageData.push({ label: name, data: saved });
          hooks.push('meta', `${label} 뒤 카메라 맞춤 ${((t1 - t0) / 1000).toFixed(1)}초 · 단계 캡처 ${((t2 - t1) / 1000).toFixed(1)}초 · ${png ? `${Math.round(png.length / 1024)}KB` : '캡처 없음'}`);
        },
        captureStep: async (step, label, names) => {
          await new Promise<void>((done) => { setTimeout(done, 250); });
          const png = snapshotPng();
          const saved: unknown = (app.dataManager as any)?.saveAsString?.();
          if (png && typeof saved === 'string') stepData.push({ step, label, names, data: saved, png, at: Math.round(performance.now() - startedAt) });
          log.push(`[meta] 풀이 ${step}단계 선언 [${names.join(',')}] 이 전부 섬 · 단계 캡처(${label})`);
        },
        captureFrame: async (names, label) => {
          await new Promise<void>((done) => { setTimeout(done, 120); });
          const png = snapshotPng();
          const saved: unknown = (app.dataManager as any)?.saveAsString?.();
          if (png && typeof saved === 'string') frameData.push({ label, names: [...names], data: saved, png, at: Math.round(performance.now() - startedAt) });
        },
        latestShot: () => snapshotPng(),
        model: port,
      };
      outcome = null;
      runDemo(app, problem, false, controller.signal, hooks, variantElements ? { elements: variantElements } : undefined)
        .then(async (stamp) => {
          if (running !== controller) return;
          // 끝 카메라로 단계를 다시 찍는다. 실패하면 단계마다 찍어 둔 것을 쓴다
          let shots = stages;
          let stepShots: StepShot[] = stepData.map((d) => ({ step: d.step, label: d.label, names: d.names, png: d.png, at: d.at }));
          let frames: FrameShot[] = frameData.map((d) => ({ label: d.label, names: d.names, png: d.png, at: d.at }));
          try {
            const again = await reshootStages(app, [...stageData, ...stepData.map((d) => ({ label: `step${d.step}`, data: d.data })), ...frameData.map((d, i) => ({ label: `frame${i + 1}`, data: d.data }))]);
            if (again.length === stageData.length + stepData.length + frameData.length) {
              shots = again.slice(0, stageData.length);
              stepShots = stepData.map((d, i) => ({ step: d.step, label: d.label, names: d.names, png: again[stageData.length + i].png, at: d.at }));
              frames = frameData.map((d, i) => ({ label: d.label, names: d.names, png: again[stageData.length + stepData.length + i].png, at: d.at }));
            }
          } catch (error) { log.push(`[error] 단계 다시 찍기 실패: ${error instanceof Error ? error.message : String(error)}`); }
          const captions = Array.isArray(meta.stageCaptions) ? meta.stageCaptions : [];
          const times = Array.isArray(meta.timeline) ? meta.timeline : [];
          const withCaptions = shots.map((st, i) => ({ ...st, caption: captions[i] ?? '', at: times.find((t) => t.label === st.label)?.at }));
          if (running === controller) { outcome = { state: 'done', stamp, meta, solution, log, stages: withCaptions, stepShots: stepShots.sort((a, b) => a.step - b.step), frames }; running = null; wake(); }
        })
        .catch((error: unknown) => { if (running === controller) { outcome = { state: 'error', message: error instanceof Error ? error.message : String(error), log }; running = null; wake(); } });
  };

  return {
    /** 새 문항으로 시작한다. 앞 실행이 있으면 멈춘다. 호출은 동기로 곧바로 돌아오고 진행은 poll 로 받는다.
     * 장면 비우기를 기다리는 약속을 돌려주면 CDP 가 그 약속을 수거한다(실측 2026-10-21 restart-probe: 「Resulting promise was garbage collected」 3회 중 1회) */
    start(problem: string, variantElements?: string[]): void {
      cancel();
      const controller = new AbortController();
      running = controller;
      outcome = null;
      void clearScene(app).then(() => { if (running === controller) launch(problem, variantElements, controller); })
        .catch((error: unknown) => { if (running === controller) { outcome = { state: 'error', message: `장면 비우기 실패: ${error instanceof Error ? error.message : String(error)}`, log: [] }; running = null; wake(); } });
    },

    /** 다음 요청이나 끝을 기다린다. 시간 안에 없으면 wait */
    async poll(timeoutMs = 60000): Promise<PipelineState> {
      const ready = (): PipelineState | null => (pending ? { state: 'ask', ...pending.ask } : outcome);
      const now = ready();
      if (now) return now;
      if (!running) return { state: 'idle' };
      await new Promise<void>((done) => { const timer = setTimeout(done, timeoutMs); waiters.push(() => { clearTimeout(timer); done(); }); });
      return ready() ?? (running ? { state: 'wait' } : { state: 'idle' });
    },

    /** 요청 id 에 대한 응답을 넣는다. json 단계의 출력은 문자열이나 객체 */
    submit(id: number, output: unknown): { ok: boolean; error?: string } {
      if (!pending) return { ok: false, error: '지금 응답을 기다리는 요청이 없다. pipeline_start 로 시작하거나 다음 요청을 받는다' };
      if (pending.ask.id !== id) return { ok: false, error: `기다리는 요청은 ${pending.ask.id} 번이다(받은 ${id} 번)` };
      const text = typeof output === 'string' ? output : JSON.stringify(output);
      const { ask, resolve } = pending;
      pending = null;
      resolve(ask.kind === 'json' ? jsonBody(text) : text);
      return { ok: true };
    },

    cancel,
  };
}
